import { RouteProp, useIsFocused } from '@react-navigation/native';
import React, { useEffect } from 'react';
import { getIntl } from '../../../../../utils/intl';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import HealthTab from './ProfileScreenContent/HealthTab';
import ManageTab from '../../../../shared/ProfileAccessContent';
import { useHasPermission, useIsLoadingPermissions } from '../../../../v2BaseComponents/Permissions/hooks';
import { ProfileTab, ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useActiveProfileTab from './useActiveProfileTab';
import { TabView, TabOption } from '../../../../v2BaseComponents/TabView';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { View } from 'react-native';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import SearchBar from '../../../../shared/SearchBar';
import ProfileActivity from '../../../../v2BaseComponents/ProfileActivity';
import { colors } from '../../../../../utils/theme';

const intl = getIntl('profileScreen', {
    headerTitle: `{name}'s Medical Profile`,
    desktopHeaderTitle: 'Medical Profile',
    editContactDetails: 'Edit Medical Profile',
    health: 'Health Records',
    connect: 'Connect',
    manage: 'Access',
    invitationDetails: 'Invitation details',
    invite: 'Invite New Collaborator',
    collaboratorDetails: 'Collaborator Details',
    deleteProfile: 'Delete Profile',
    removeSelfAsCollaborator: 'Leave Profile',
    inviteCollaborator: 'Invite Collaborator',
    editCard: 'Edit Card',
    addCard: 'Add Card',
    resourceList: 'Data',
    resourceDetails: 'Details',
    createResource: 'Create',
    editResource: 'Edit',
    image: 'Image',
    addPatientPortal: 'Add Patient Portal',
    refresh: 'Refresh Patient Portals',
    note: 'Note',
    addNote: 'Add Note',
    editNote: 'Edit Note',
    transferNote: 'Move Note',
    notes: 'Notes',
    defaultHeader: 'Profile',
    dashboard: 'Dashboard',
});

export type ProfileScreenOwnProps = {};

export type ProfileScreenProps = ProfileScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'profile'>;
};

const ProfileScreen: React.FC<ProfileScreenProps> = ({ route }) => {
    const isMobile = useIsMobileLayout();
    const isFocused = useIsFocused();
    const navigation = useProfilesTabNavigation();
    useEffect(() => {
        if (!isMobile && isFocused) {
            setTimeout(() => {
                navigation.replace('profile-activity', { profileId: route.params.profileId });
            }, 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);
    const { profileId } = route.params || {};
    const { profile, isLoadingProfile } = useMedicalProfile({ medicalProfileId: profileId });
    const isLoadingPermissions = useIsLoadingPermissions(profileId);
    const hasCollaboratorViewPermission = useHasPermission(profileId, 'collaborators', 'view');
    const hasIdAndInfoViewPermission = useHasPermission(profileId, 'idsAndInfo', 'view');
    const canViewManageTab = hasCollaboratorViewPermission && hasIdAndInfoViewPermission;
    const { activeTab, setActiveTab } = useActiveProfileTab({
        profileId,
        canViewManageTab,
        defaultTab: route.params.defaultTab,
    });

    const tabs: TabOption[] = [
        {
            title: intl('dashboard'),
            value: 'dashboard',
            renderTabContent: () => <ProfileActivity profileId={profileId} />,
        },
        {
            title: intl('health'),
            value: 'health',
            renderTabContent: () => <HealthTab profileId={profileId} />,
        },
        {
            title: intl('manage'),
            value: 'manage',
            hide: !canViewManageTab,
            renderTabContent: () => <ManageTab medicalProfileId={profileId} />,
        },
    ];

    return (
        <AnimateLoadedContent isLoading={isLoadingProfile || !profile || isLoadingPermissions}>
            {() => (
                <ProfileHeaderContainer profileId={profileId}>
                    <SearchBar medicalProfileId={route.params.profileId} style={{ backgroundColor: colors['White'] }} />
                    <TabView tabs={tabs} selectedTab={activeTab} onChange={(_) => setActiveTab(_ as ProfileTab)} />
                    {/* this is gross, but e2e tests a lot of payment status functionality.  We are adding premium features soon and can validate based on that */}
                    <View style={{ width: 0, height: 0 }} collapsable={false} testID={`payment-status-paid`} />
                </ProfileHeaderContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ProfileScreen);
